import * as Yup from 'yup';
import { passwordParameters } from '@shared/services';

/**
 * Builds the Yup rule for a new password from the shared password policy
 * (`passwordParameters` / `PASSWORD_POLICY_VERSION`) so the client cannot drift from the backend.
 * Used by both the recovery and forced-change flows, which must target the current policy.
 */
export const buildNewPasswordRule = (): Yup.StringSchema<string> => {
  const { min, max, match } = passwordParameters.password;

  let rule = Yup.string().required('Password is required');

  if (min !== undefined) {
    rule = rule.min(min, `Password must be at least ${min} characters`);
  }

  if (max !== undefined) {
    rule = rule.max(max, `Password must be at most ${max} characters`);
  }

  return rule.matches(match.pattern, match.message);
};

/** A single new-password rule, surfaced as a live green/red marker in the UI. */
export interface PasswordRequirement {
  label: string;
  /** True when `value` satisfies this rule. */
  test: (value: string) => boolean;
}

/**
 * The password policy broken into individual, human-readable rules for the live requirements
 * checklist. Length rules read `min`/`max` from the shared {@link passwordParameters} so they never
 * drift; the character-class rules mirror the policy's single combined pattern (which cannot be
 * cleanly split) as app-local checks. Presentation-only — the authoritative gate stays
 * {@link buildNewPasswordRule}.
 */
export const passwordRequirements: PasswordRequirement[] = (() => {
  const { min, max } = passwordParameters.password;
  const rules: PasswordRequirement[] = [];

  if (min !== undefined) {
    rules.push({ label: `Minimum ${min} characters`, test: (value) => value.length >= min });
  }
  if (max !== undefined) {
    rules.push({ label: `No more than ${max} characters`, test: (value) => value.length <= max });
  }

  rules.push(
    { label: 'At least one number', test: (value) => /[0-9]/.test(value) },
    { label: 'At least one lowercase letter', test: (value) => /[a-z]/.test(value) },
    { label: 'At least one uppercase letter', test: (value) => /[A-Z]/.test(value) },
    {
      label: 'Only latin letters, digits and ! @ # $ % & * ? _ . -',
      test: (value) => /^[A-Za-z0-9!@#$%&*?_. -]*$/.test(value),
    },
  );

  return rules;
})();
